import { FC, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { BUSINESS_TOOL_MODE } from 'constants/businessTool';
import { BUSINESS_TOOL } from 'constants/paths';
import { WorkspaceLayout } from 'layouts/WorkspaceLayout';
import { useCreateBusinessPlan, useGetAllBusinessPlans } from 'services/businessPlan/hooks';
import { useGetSubscriptionDetails } from 'services/payments/subscriptions/hooks';
import { getDefaultBusinessPlan, useBusinessToolSessionStore } from 'store/useBusinessToolStore';
import { getSetBusinessToolMode } from 'store/useBusinessToolStore/selectors';
import { useUserStore } from 'store/useUserStore';
import { getAuthUser } from 'store/useUserStore/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { IBusinessPlanSectionEntity, IBusinessPlanType } from 'interfaces/businessPlan';
import { IUserSubscriptionResponse } from 'interfaces/subscriptions';

import { PageLoader } from 'components/_basic/PageLoader/PageLoader';

import { insertId } from 'helpers/path';

export const Workspace: FC = () => {
  const { data: businessPlansData, error, isPending } = useGetAllBusinessPlans();
  const authUser = useUserStore(getAuthUser);
  const { isAuthenticated } = useAuthenticated();
  const setBusinessToolMode = useBusinessToolSessionStore(getSetBusinessToolMode);
  const [searchParams] = useSearchParams();
  const createBusinessPlan = useCreateBusinessPlan();
  const defaultBusinessPlan = getDefaultBusinessPlan();
  const navigate = useNavigate();

  const { data: subscriptionData } = useGetSubscriptionDetails({ enabled: !!authUser && isAuthenticated });

  const [loading, setLoading] = useState(true);

  const [sections, setSections] = useState<IBusinessPlanSectionEntity[] | null>(null);

  const projectBriefDescription = localStorage.getItem('projectBriefDescription');
  const companyBriefDescription = localStorage.getItem('companyBriefDescription');
  const address = localStorage.getItem('address');
  const companyName = localStorage.getItem('companyName');
  const onlyPayedSignUp = localStorage.getItem('onlyPayedSignUp');

  useEffect(() => {
    const newObj: Record<string, string> = {};
    const language = localStorage.getItem('language');

    if (projectBriefDescription) {
      newObj['projectBriefDescription'] = projectBriefDescription;
    }
    if (companyBriefDescription) {
      newObj['companyBriefDescription'] = companyBriefDescription;
    }
    if (address) {
      newObj['address'] = address;
    }
    if (companyName) {
      newObj['companyName'] = companyName;
    }
    if (language) {
      newObj['documentLanguage'] = language === 'de' ? 'Deutsch' : 'English';
    }

    const formattedSections: IBusinessPlanSectionEntity[] = [
      { chapterName: 'GLOBAL', content: JSON.stringify(newObj) },
    ];
    setSections(formattedSections);
    return () => {
      setSections([]);
    };
  }, []);

  const createBP = useCallback(
    async ({ type, sections }: { type: IBusinessPlanType; sections: IBusinessPlanSectionEntity[] }) => {
      try {
        const newBusinessPlan = await createBusinessPlan.mutateAsync({
          title: 'Document',
          type,
          sections,
          metadata: defaultBusinessPlan.metadata,
        });

        navigate(insertId(BUSINESS_TOOL, newBusinessPlan.uuid));
        localStorage.removeItem('typeFormDataId');
      } catch (error) {
        console.error(error);
      }
    },
    [sections],
  );

  useEffect(() => {
    if (onlyPayedSignUp) {
      return;
    }
    if (
      businessPlansData &&
      (subscriptionData as IUserSubscriptionResponse)?.currentSubscription?.subscriptionPlan === 'FREE' &&
      !businessPlansData?.content?.find((item) => item.type === 'BANK' || item.type === 'LIGHT') &&
      sections
    ) {
      setBusinessToolMode(BUSINESS_TOOL_MODE.EDIT);
      createBP({ type: 'BANK', sections: sections });
    } else if (
      searchParams.get('newTarif') &&
      subscriptionData &&
      businessPlansData &&
      (subscriptionData as IUserSubscriptionResponse)?.currentSubscription?.subscriptionPlan !== 'STARTER' &&
      !businessPlansData?.content?.find((item) => item.type === 'BANK' || item.type === 'LIGHT') &&
      sections
    ) {
      setBusinessToolMode(BUSINESS_TOOL_MODE.EDIT);
      createBP({ type: 'BANK', sections: sections });
    } else if (subscriptionData && businessPlansData) {
      setLoading(false);
    }
  }, [searchParams.get('newTarif'), subscriptionData, businessPlansData, sections]);

  return (
    <>
      <Helmet>
        <script type="text/javascript" defer>
          {`
            window.dataLayer = window.dataLayer || [];
            let userId = '';
            try {
              const userStore = localStorage.getItem('user-store');
              if (userStore) {
                const parsed = JSON.parse(userStore);
                userId = parsed?.state?.authUser?.id || '';
                dataLayer.push({ user_id: userId });
              }
            } catch (e) {
              console.error('Failed to parse user-store:', e);
            }
          `}
        </script>
      </Helmet>
      {loading ? <PageLoader /> : <WorkspaceLayout data={businessPlansData} isLoading={isPending} error={error} />}
    </>
  );
};
